import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import type { DocumentTypeEntry } from "../types";
import { documentIcon } from "./documentIcons";

export interface DocumentTileProps {
  entry: DocumentTypeEntry;
  checked: boolean;
  disabled: boolean;
  onToggle: (key: string) => void;
  /** A small square from `sm` up, and below it a row: the agent's flow card on a phone. */
  compact?: boolean;
}

/** The frame shared by the request grid's tiles: a square, or with `compact` a row on a phone. */
export function tileClass(active: boolean, disabled: boolean, compact = false): string {
  return cn(
    "relative flex cursor-pointer rounded-xl border bg-background transition-colors",
    compact
      ? "min-h-11 items-center gap-2 px-2.5 py-2 text-left sm:aspect-square sm:flex-col sm:justify-center sm:p-2 sm:text-center"
      : "aspect-square flex-col items-center justify-center gap-2 p-3 text-center",
    "hover:border-foreground/25 hover:bg-muted/30",
    active && "border-primary bg-primary/5 hover:border-primary hover:bg-primary/10",
    disabled && "cursor-not-allowed opacity-60"
  );
}

/**
 * Where a tile's marks sit (checkbox, Other's count and "+"): the square's corners, or when
 * compact the row's end below `sm`. Compact, the checkbox carries data-compact, whose touch rule
 * sets position: relative (index.css); hence its !absolute.
 */
const TILE_CORNERS = {
  checkbox: {
    square: "absolute right-2.5 top-2.5",
    compact: "max-sm:order-last sm:!absolute sm:right-2.5 sm:top-2.5",
  },
  count: {
    square: "absolute left-2 top-2",
    compact: "max-sm:order-last sm:absolute sm:left-2 sm:top-2",
  },
  add: {
    square: "absolute right-2 top-2",
    compact: "max-sm:order-last sm:absolute sm:right-2 sm:top-2",
  },
} as const;

export function tileCornerClass(mark: keyof typeof TILE_CORNERS, compact = false): string {
  return TILE_CORNERS[mark][compact ? "compact" : "square"];
}

/** A tile's document icon: leading the row when compact. */
export function tileIconClass(active: boolean, compact = false): string {
  return cn(
    compact ? "size-4 shrink-0 sm:size-5" : "h-8 w-8",
    "text-muted-foreground",
    active && "text-primary"
  );
}

/** A tile's label: filling the row when compact, wrapping as it needs. */
export function tileLabelClass(compact = false): string {
  return cn(
    "font-medium leading-tight",
    compact ? "min-w-0 flex-1 break-words text-xs sm:flex-none sm:text-[11px]" : "text-sm"
  );
}

/** One tile of the request grid; the whole tile toggles its checkbox. */
const DocumentTile = React.memo(function DocumentTile({
  entry,
  checked,
  disabled,
  onToggle,
  compact = false,
}: DocumentTileProps) {
  const Icon = documentIcon(entry.icon);
  const id = `request-document-${entry.key}`;
  return (
    <label
      htmlFor={id}
      data-state={checked ? "checked" : "unchecked"}
      className={cn(
        tileClass(checked, disabled, compact),
        "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
      )}
    >
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={() => onToggle(entry.key)}
        // Compact, the row is the touch target: data-compact keeps the box 16px on touch.
        data-compact={compact ? "" : undefined}
        className={tileCornerClass("checkbox", compact)}
      />
      <Icon className={tileIconClass(checked, compact)} aria-hidden />
      <span className={tileLabelClass(compact)}>{entry.label}</span>
    </label>
  );
});

export default DocumentTile;
